import React from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import WhatsAppFilters from './WhatsAppFilters';
import WhatsAppCompanyRow from './WhatsAppCompanyRow';
import PaginationFooter from '../../components/PaginationFooter';

const HEADERS = [
  { label: '', align: 'left' },
  { label: 'Empresa', align: 'left' },
  { label: 'Origem atual', align: 'left' },
  { label: 'Envios plataforma', align: 'center' },
  { label: 'Operacionais / Demais', align: 'center' },
  { label: 'Envios próprio', align: 'center' },
  { label: 'Bloqueados', align: 'center' },
  { label: 'Alertas de limite', align: 'center' },
  { label: 'Último envio', align: 'left' },
];

/**
 * WhatsAppCompaniesTable - Tabela de empresas com consumo de WhatsApp no período, filtros
 * e paginação server-side (Admin Geral > WhatsApp). Cada linha é expansível (WhatsAppCompanyRow)
 * e mostra o detalhamento de envios por tipo de mensagem da empresa.
 * @param {Object} props
 * @param {Array} props.companies
 * @param {boolean} props.isLoading
 * @param {string|null} props.error
 * @param {Object} props.pagination - { total, page, limit, pages }
 * @param {Array<{key: string, label: string}>} props.categories - ordem/labels vinda do overview/companies
 * @param {Object} props.filters - props repassadas ao WhatsAppFilters
 * @param {Function} props.onPageChange
 */
function WhatsAppCompaniesTable({
  companies = [],
  isLoading = false,
  error = null,
  pagination,
  categories = [],
  filters,
  onPageChange,
}) {
  return (
    <Paper
      sx={{
        borderRadius: '12px',
        boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        border: '1px solid #E8ECF0',
        overflow: 'hidden',
      }}
    >
      <WhatsAppFilters {...filters} />

      {error && (
        <Alert severity="error" sx={{ borderRadius: 0 }}>
          {error}
        </Alert>
      )}

      <TableContainer sx={{ boxShadow: 'none', borderRadius: 0 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#F8F9FC' }}>
              {HEADERS.map((h, index) => (
                <TableCell
                  key={h.label || `col-${index}`}
                  align={h.align}
                  sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.8125rem', whiteSpace: 'nowrap' }}
                >
                  {h.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : companies.length === 0 ? (
              <TableRow>
                <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 6 }}>
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma empresa com envios no período.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              companies.map((company) => (
                <WhatsAppCompanyRow
                  key={company.company_id}
                  company={company}
                  categories={categories}
                  days={filters?.days}
                />
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <PaginationFooter pagination={pagination} onPageChange={onPageChange} itemLabel="empresas" />
    </Paper>
  );
}

export default WhatsAppCompaniesTable;
